import { useNavigate } from 'react-router-dom';
import { ApolloError } from '@apollo/client';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Pagination,
  IPaginationProps,
  LinearProgress,
  LoadedContent,
} from '@occc/ui-design-system';
import { SegmentFragment } from '@occc/graphql';

import { SegmentListActionsController } from './segment-list-actions.controller';

import { routeCreator } from '../../segments.router';

type SegmentListTableProps = {
  segments: SegmentFragment[] | undefined;
  loading: boolean;
  error: ApolloError | undefined;
  paginationProps: IPaginationProps;
  isDeleteAllowed: boolean;
};

export const SegmentListTable = ({
  segments,
  loading,
  error,
  paginationProps,
  isDeleteAllowed,
}: SegmentListTableProps) => {
  const navigate = useNavigate();
  const hasAtLeastOneAction = isDeleteAllowed;

  const renderData = (items: SegmentFragment[]) => {
    const rows = items.map((segment) => {
      const onClick = () => navigate(routeCreator.details(segment.uuid));

      return (
        <TableRow key={segment.uuid} onClick={onClick}>
          <TableCell>{segment.name}</TableCell>
          <TableCell>{segment.description}</TableCell>
          <TableCell>{segment.membershipCount}</TableCell>

          {hasAtLeastOneAction && (
            <TableCell padding='button' align='right'>
              <SegmentListActionsController
                uuid={segment.uuid}
                name={segment.name}
                isDeleteAllowed={isDeleteAllowed}
              />
            </TableCell>
          )}
        </TableRow>
      );
    });

    return (
      <>
        {loading && <LinearProgress />}

        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Size</TableCell>
              {hasAtLeastOneAction && <TableCell align='right'>Actions</TableCell>}
            </TableRow>
          </TableHead>

          <TableBody>{rows}</TableBody>
        </Table>
        <Pagination {...paginationProps} />
      </>
    );
  };

  return (
    <LoadedContent<SegmentFragment>
      error={error}
      loading={loading}
      items={segments}
      renderData={renderData}
    />
  );
};
